import useMountEffect from '@restart/hooks/useMountEffect';
import useUpdateEffect from '@restart/hooks/useUpdateEffect';
import classNames from 'classnames/bind';
import React, { useState, useRef } from 'react';
import { Joystick } from 'react-joystick-component';

import styles from './index.module.scss';

import BinarySwitch from '@/components/ui/BinarySwitch';
import { MODE } from '@/define/robotStatus';
import useActionCommand from '@/hooks/command/mavlink/useActionCommand';
import useMavLinkAddress from '@/hooks/useMavLinkAddress';
import useMavLinkCommandHandler from '@/hooks/useMavLinkCommandHandler';
import EventEmitter from '@/libs/EventEmitter';

const cx = classNames.bind(styles);

const Panel = ({ data: [robot] }) => {
  const { getAddress } = useMavLinkAddress();
  const [isVirtualJoystick, setIsVirtualJoystick] = useState(true);
  const [joystickLeftPosition, setJoystickLeftPosition] = useState({ x: 0, y: 0 });
  const [joystickRightPosition, setJoystickRightPosition] = useState({ x: 0, y: 0 });
  const stickControl = useRef({ x: 0, y: 0, z: 500, r: 0 });

  const actionCommand = useActionCommand();
  const { addInterval, clearIntervals } = useMavLinkCommandHandler(robot);

  useMountEffect(() => {
    actionCommand.setMode(MODE.ARDUPILOT.COPTER.LOITER.id)(robot).execute();
    EventEmitter.publish(`${robot.id}/event/removeGoto`);

    addInterval(
      'action/stick_control',
      () => [
        [
          getAddress(robot.id)[0],
          stickControl.current.x,
          stickControl.current.y,
          stickControl.current.z,
          stickControl.current.r,
        ],
      ],
      50,
    );

    return () => {
      clearIntervals();
      actionCommand.setMode(MODE.ARDUPILOT.COPTER.GUIDED.id)(robot).execute();
    };
  });

  useUpdateEffect(() => {
    if (isVirtualJoystick) return;

    let frameId;
    const handleJoystick = () => {
      const joystick = navigator.getGamepads()[0];
      if (joystick) {
        moveJoystickLeft({
          x: joystick.axes[0],
          y: joystick.axes[1],
        });
        moveJoystickRight({
          x: joystick.axes[2],
          y: joystick.axes[3],
        });
      }

      frameId = window.requestAnimationFrame(handleJoystick);
    };

    handleJoystick();

    return () => {
      window.cancelAnimationFrame(frameId);
      stopJoystickLeft();
      stopJoystickRight();
    };
  }, [isVirtualJoystick]);

  const selectSwitch = (e) => {
    setIsVirtualJoystick(e.currentTarget.dataset.value === 'true');
  };

  const moveJoystickLeft = ({ x, y }) => {
    stickControl.current.r = Math.trunc(x * 1000);
    stickControl.current.z = Math.trunc(((y + 1) / 2) * 1000); // z 범위: 0 ~ 1000, 중립: 500

    setJoystickLeftPosition({ x, y });
  };

  const stopJoystickLeft = () => {
    stickControl.current.r = 0;
    stickControl.current.z = 500;

    setJoystickLeftPosition({ x: 0, y: 0 });
  };

  const moveJoystickRight = ({ x, y }) => {
    stickControl.current.x = Math.trunc(y * 1000);
    stickControl.current.y = Math.trunc(x * 1000);

    setJoystickRightPosition({ x, y });
  };

  const stopJoystickRight = () => {
    stickControl.current.x = 0;
    stickControl.current.y = 0;

    setJoystickRightPosition({ x: 0, y: 0 });
  };

  return (
    <div className={cx('container')}>
      <BinarySwitch
        items={[
          { title: 'Virtual', value: true, onClick: selectSwitch },
          { title: 'Real', value: false, onClick: selectSwitch },
        ]}
      />
      <div className={cx('joysticks')}>
        <div className={cx(['joystick', { inactive: !isVirtualJoystick }])}>
          <Joystick
            size={120}
            stickSize={64}
            sticky={false}
            stickImage={require('./joystick-blue.png')}
            baseImage={require('./joystick-base.png')}
            move={moveJoystickLeft}
            stop={stopJoystickLeft}
            pos={joystickLeftPosition}
            disabled={!isVirtualJoystick}
          />
          <div className={cx('info')}>
            <div className={cx('item')}>{joystickLeftPosition.x.toFixed(2)}</div>
            <div className={cx('item')}>{joystickLeftPosition.y.toFixed(2)}</div>
          </div>
        </div>
        <div className={cx(['joystick', { inactive: !isVirtualJoystick }])}>
          <Joystick
            size={120}
            stickSize={64}
            sticky={false}
            stickImage={require('./joystick-blue.png')}
            baseImage={require('./joystick-base.png')}
            move={moveJoystickRight}
            stop={stopJoystickRight}
            pos={joystickRightPosition}
            disabled={!isVirtualJoystick}
          />
          <div className={cx('info')}>
            <div className={cx('item')}>{joystickRightPosition.x.toFixed(2)}</div>
            <div className={cx('item')}>{joystickRightPosition.y.toFixed(2)}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Panel;
